import React from 'react';
import { motion } from 'framer-motion';
import { Banknote, Target, Building2, Rocket, ArrowRight } from 'lucide-react';

function AskSection({ content }) {
  const useOfFunds = [
    { category: 'Product Development', amount: '$175,000', percent: 35, color: 'kore' },
    { category: 'Marketing & Growth', amount: '$150,000', percent: 30, color: 'lavender' },
    { category: 'Operations', amount: '$100,000', percent: 20, color: 'mint' },
    { category: 'Reserve', amount: '$75,000', percent: 15, color: 'gold' },
  ];

  const milestones = [
    { month: 'Month 6', target: 'MVP launched, 2,500 users, product-market fit signals' },
    { month: 'Month 12', target: '10,000 users, $50K MRR' },
    { month: 'Month 18', target: '25,000 users, $100K MRR, positioned for Series A' },
  ];

  const exitOpportunities = [
    { type: 'Salon Software', examples: 'Boulevard, Mindbody, Zenoti' },
    { type: 'Job Platforms', examples: 'Indeed, LinkedIn, ZipRecruiter' },
    { type: 'Beauty Conglomerates', examples: "L'Oréal, Henkel, Coty" },
    { type: 'Private Equity', examples: 'Roll-up strategy' },
  ];

  const colorClasses = {
    kore: 'bg-kore-500',
    lavender: 'bg-accent-lavender',
    mint: 'bg-accent-mint',
    gold: 'bg-accent-gold',
  };

  return (
    <section className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-20">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent-gold/10 border border-accent-gold/20 rounded-full mb-6">
            <Banknote className="w-4 h-4 text-accent-gold" />
            <span className="text-sm text-accent-gold">The Ask</span>
          </div>
          <h2 className="section-title mb-4">
            <span className="text-gradient">$500,000</span> Seed Round
          </h2>
          <p className="section-subtitle mx-auto">
            18 months runway to product-market fit and Series A positioning.
          </p>
        </motion.div>

        {/* Main Investment Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card bg-gradient-to-br from-kore-900/50 via-surface-card to-accent-lavender/10 border-kore-500/30 mb-8"
        >
          <div className="grid md:grid-cols-2 gap-8">
            {/* Amount */}
            <div className="text-center md:text-left">
              <div className="text-6xl md:text-7xl font-bold text-gradient mb-2">$500K</div>
              <div className="text-zinc-400">Seed Investment</div>
              <div className="mt-4 flex flex-wrap gap-3 justify-center md:justify-start">
                <span className="px-3 py-1 bg-surface-elevated rounded-full text-sm text-zinc-300">
                  18 months runway
                </span>
                <span className="px-3 py-1 bg-surface-elevated rounded-full text-sm text-zinc-300">
                  SAFE or Equity
                </span>
              </div>
            </div>

            {/* Use of Funds */}
            <div>
              <h3 className="text-sm font-medium text-zinc-400 mb-4">Use of Funds</h3>
              <div className="space-y-3">
                {useOfFunds.map((item, index) => (
                  <motion.div
                    key={item.category}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                  >
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-zinc-300">{item.category}</span>
                      <span className="text-white font-medium">{item.amount}</span>
                    </div>
                    <div className="h-2 bg-surface-elevated rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${item.percent}%` }}
                        transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
                        className={`h-full rounded-full ${colorClasses[item.color]}`}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Milestones */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className="card"
          >
            <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
              <Target className="w-5 h-5 text-kore-400" />
              Milestones Funded
            </h3>
            <div className="space-y-4">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={milestone.month}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <div className="flex-shrink-0 w-20 text-sm font-medium text-kore-400">
                    {milestone.month}
                  </div>
                  <div className="flex-1 text-sm text-zinc-400">{milestone.target}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Exit Opportunities */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.7 }}
            className="card"
          >
            <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-accent-mint" />
              Exit Opportunities
            </h3>
            <div className="space-y-3">
              {exitOpportunities.map((exit, index) => (
                <motion.div
                  key={exit.type}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 + index * 0.1 }}
                  className="flex items-center justify-between py-2 border-b border-surface-border/50 last:border-0"
                >
                  <span className="text-sm font-medium text-white">{exit.type}</span>
                  <span className="text-xs text-zinc-500">{exit.examples}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="text-center mt-12"
        >
          <a
            href="mailto:invest@korefolio.com"
            className="inline-flex items-center gap-2 btn-primary text-lg px-8 py-4"
          >
            <Rocket className="w-5 h-5" />
            Let's Talk
            <ArrowRight className="w-5 h-5" />
          </a>
          <p className="text-sm text-zinc-500 mt-4">
            invest@korefolio.com
          </p>
        </motion.div>
      </div>
    </section>
  );
}

export default AskSection;
